/**
 * Hub API — data layer for the operator web panel.
 *
 * MODE SWITCH:
 *   HUB_API_MODE = 'mock'  → returns data from mockData.ts (no HTTP, no backend needed)
 *   HUB_API_MODE = 'http'  → fetches from HUB_API_BASE_URL (set via VITE_HUB_API_URL)
 *
 * To connect to the real backend after server provisioning:
 *   1. Set VITE_HUB_API_URL=https://hub.zov-mebel.ru  in .env.production
 *   2. Change HUB_API_MODE below to 'http'  (or control via import.meta.env.VITE_HUB_API_MODE)
 *   3. No other files need to change.
 *
 * Persistence:
 *   Auth token and user are stored in localStorage so that a page refresh
 *   does not log the operator out.
 *   Operator availability (shift status) is also persisted in localStorage
 *   so that "На смене" / "Перерыв" survive a refresh.
 *   Logout clears all persisted keys.
 *
 * Limitation (MVP): server-side availability state is in-memory in hub-api and
 *   resets on backend restart. True durable status should later be stored in
 *   PostgreSQL (hub_operator_availability table) — see availability.ts note.
 */

import {
  getMockClientCard,
  getMockKpi,
  getMockMessages,
  getMockRoutingTimeline,
  MOCK_BOT_SCENARIO,
  MOCK_CHANNELS,
  MOCK_DIALOGS,
  MOCK_AUDIT_LOG,
  MOCK_OUTBOX,
  mockAssign,
  mockClose,
  mockEscalate,
  mockSendMessage,
  mockTransfer,
  runMockTelegramDemo,
} from './mockData';
import type {
  AdminSettingsSummary,
  AdminDashboard,
  AdminChannel,
  AdminStats,
  AdminSystemStatus,
  AdminDialogEntry,
  ApiResponse,
  BotScenario,
  DashboardKpi,
  MeResponse,
  OperatorChannel,
  OperatorClientCard,
  OperatorDashboard,
  OperatorDialog,
  OperatorMessage,
  MockAuditEvent,
  MockOutboxMessage,
  QueueResponse,
  MvpSettingsSummary,
  ReadinessSummary,
  RoleCode,
  RoutingTimeline,
  SupervisorDashboard,
  TelegramDemoResult,
  TelegramReadiness,
} from './types';
import {
  getMockAdminSummary,
  getMockMe,
  getMockSupervisorDashboard,
} from './mockRoles';

// ─── Configuration ────────────────────────────────────────────────────────────

/** 'mock' | 'http' — defaults to 'http' in production; set VITE_HUB_API_MODE=mock for standalone demo */
const HUB_API_MODE: 'mock' | 'http' =
  (import.meta.env.VITE_HUB_API_MODE as 'mock' | 'http') ?? 'http';

/**
 * API base URL. Defaults to '' (empty string = same origin, relative /api/...).
 * Override with VITE_HUB_API_URL if the backend is on a different host.
 */
const HUB_API_BASE_URL: string =
  import.meta.env.VITE_HUB_API_URL ?? '';

// ─── Internal helpers ─────────────────────────────────────────────────────────

/** localStorage key constants — single source of truth. */
const LS_TOKEN = 'hub_auth_token';
const LS_USER  = 'hub_auth_user';
const LS_AVAIL = 'hub_operator_availability';
const LS_VIEW  = 'ohub.currentView';

/**
 * Bearer JWT — initialised from localStorage so a page refresh restores auth.
 * Updated by setAuthToken(); cleared by clearPersistedSession().
 */
let _authToken: string | null = (() => {
  try { return localStorage.getItem(LS_TOKEN); } catch { return null; }
})();

export function setAuthToken(token: string | null): void {
  _authToken = token;
  try {
    if (token) {
      localStorage.setItem(LS_TOKEN, token);
    } else {
      localStorage.removeItem(LS_TOKEN);
    }
  } catch { /* storage unavailable — fail silently */ }
}

export function getAuthToken(): string | null {
  return _authToken;
}

/** Persist the logged-in user object so it survives a refresh. */
export function persistAuthUser(user: object | null): void {
  try {
    if (user) {
      localStorage.setItem(LS_USER, JSON.stringify(user));
    } else {
      localStorage.removeItem(LS_USER);
    }
  } catch { /* ignore */ }
}

/** Read the persisted user object from localStorage. Returns null if absent. */
export function getPersistedAuthUser<T>(): T | null {
  try {
    const raw = localStorage.getItem(LS_USER);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

/** Persist operator availability status across refreshes. */
export function persistAvailabilityStatus(status: string | null): void {
  try {
    if (status) {
      localStorage.setItem(LS_AVAIL, status);
    } else {
      localStorage.removeItem(LS_AVAIL);
    }
  } catch { /* ignore */ }
}

/** Read the persisted availability status. Returns null if absent. */
export function getPersistedAvailabilityStatus(): string | null {
  try {
    return localStorage.getItem(LS_AVAIL);
  } catch {
    return null;
  }
}

/** Persist the current UI view so it survives a page refresh. */
export function persistCurrentView(view: string | null): void {
  try {
    if (view) {
      localStorage.setItem(LS_VIEW, view);
    } else {
      localStorage.removeItem(LS_VIEW);
    }
  } catch { /* ignore */ }
}

/** Read the persisted view from localStorage. Returns null if absent. */
export function getPersistedCurrentView(): string | null {
  try {
    return localStorage.getItem(LS_VIEW);
  } catch {
    return null;
  }
}

/** Clear ALL session data from localStorage (called on logout). */
export function clearPersistedSession(): void {
  setAuthToken(null);
  persistAuthUser(null);
  persistAvailabilityStatus(null);
  persistCurrentView(null);
}

function authHeaders(): Record<string, string> {
  return _authToken ? { Authorization: `Bearer ${_authToken}` } : {};
}

function mockResponse<T>(data: T): ApiResponse<T> {
  return { data, _source: 'mock' };
}

async function httpGet<T>(path: string): Promise<ApiResponse<T>> {
  const res = await fetch(`${HUB_API_BASE_URL}${path}`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`Hub API error ${res.status}: ${path}`);
  const json = await res.json();
  // Support both {data: T} envelope and bare T responses
  if (json && typeof json === 'object' && 'data' in json) {
    return json as ApiResponse<T>;
  }
  return { data: json as T, _source: 'live' };
}

async function httpPost<T>(path: string, body: unknown): Promise<ApiResponse<T>> {
  const res = await fetch(`${HUB_API_BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`Hub API error ${res.status}: POST ${path}`);
  return res.json() as Promise<ApiResponse<T>>;
}

// ─── Auth (login + me) ───────────────────────────────────────────────────────

export interface LoginResponse {
  token: string;
  expiresAt: string;
  user: { id: string; name: string; role: string; shopId: string | null; shopName: string | null };
}

/** Test-contour login. Returns the session and stores the token in memory. */
export async function loginAs(login: string, password: string): Promise<ApiResponse<LoginResponse>> {
  if (HUB_API_MODE === 'mock') {
    // Mock: synthesise a fake JWT-shaped string so the UI flow works offline.
    const token = `mock.${btoa(JSON.stringify({ sub: login, role: login, name: login }))}.demo`;
    setAuthToken(token);
    return mockResponse<LoginResponse>({
      token,
      expiresAt: new Date(Date.now() + 8 * 3600_000).toISOString(),
      user: { id: `mock-${login}`, name: login, role: login, shopId: null, shopName: null },
    });
  }
  const res = await httpPost<LoginResponse>('/api/v1/auth/login', { login, password });
  setAuthToken(res.data.token);
  return res;
}

export function logoutLocal(): void {
  clearPersistedSession();
}

// ─── Operator availability (Начать работу / Перерыв / Закончить работу) ────

import type {
  OperatorAvailabilityAction,
  OperatorAvailabilityState,
} from './types';

/** Локальный fallback на mock-режим — храним статусы в module memory. */
const _mockAvailability = new Map<string, OperatorAvailabilityState>();

function _mockGetAvailability(operatorId: string): OperatorAvailabilityState {
  const existing = _mockAvailability.get(operatorId);
  if (existing) return existing;
  const fresh: OperatorAvailabilityState = {
    operatorId,
    operatorName: null,
    crmShopId: null,
    status: 'off',
    since: new Date().toISOString(),
    history: [],
  };
  _mockAvailability.set(operatorId, fresh);
  return fresh;
}

export async function fetchOperatorAvailability(
  operatorId: string
): Promise<ApiResponse<OperatorAvailabilityState>> {
  if (HUB_API_MODE === 'mock') {
    return mockResponse(_mockGetAvailability(operatorId));
  }
  return httpGet<OperatorAvailabilityState>(
    `/api/v1/operator/availability/${encodeURIComponent(operatorId)}`
  );
}

export async function setOperatorAvailability(input: {
  operatorId: string;
  operatorName?: string | null;
  crmShopId?: number | null;
  action: OperatorAvailabilityAction;
}): Promise<ApiResponse<OperatorAvailabilityState>> {
  if (HUB_API_MODE === 'mock') {
    const rec = _mockGetAvailability(input.operatorId);
    const ACTION_TO_STATUS = {
      start_work: 'available' as const,
      break: 'break' as const,
      end_work: 'off' as const,
    };
    const next = ACTION_TO_STATUS[input.action];
    if (rec.status !== next) {
      rec.history.push({ from: rec.status, to: next, at: new Date().toISOString() });
      rec.status = next;
      rec.since = new Date().toISOString();
    }
    if (input.operatorName) rec.operatorName = input.operatorName;
    if (input.crmShopId !== undefined) rec.crmShopId = input.crmShopId;
    return mockResponse(rec);
  }
  return httpPost<OperatorAvailabilityState>('/api/v1/operator/availability', input);
}

// ─── Public API — reads ────────────────────────────────────────────────────────

/** Returns the data source label shown in the UI. */
export function getApiSourceLabel(): string {
  return HUB_API_MODE === 'mock' ? 'API mock — готово к подключению backend' : 'Backend подключён';
}

/** Whether the panel is operating in mock mode. */
export function isApiMock(): boolean {
  return HUB_API_MODE === 'mock';
}

export async function fetchDashboard(): Promise<ApiResponse<OperatorDashboard>> {
  if (HUB_API_MODE === 'mock') {
    return mockResponse<OperatorDashboard>({
      kpi: getMockKpi(),
      dialogs: MOCK_DIALOGS,
      channels: MOCK_CHANNELS,
      botScenario: MOCK_BOT_SCENARIO,
    });
  }
  return httpGet<OperatorDashboard>('/api/v1/operator/dashboard');
}

export async function fetchKpi(): Promise<ApiResponse<DashboardKpi>> {
  if (HUB_API_MODE === 'mock') return mockResponse(getMockKpi());
  return httpGet<DashboardKpi>('/api/v1/operator/kpi');
}

export async function fetchDialogs(): Promise<ApiResponse<OperatorDialog[]>> {
  if (HUB_API_MODE === 'mock') return mockResponse([...MOCK_DIALOGS]);
  return httpGet<OperatorDialog[]>('/api/v1/operator/dialogs');
}

export async function fetchDialog(id: string): Promise<ApiResponse<OperatorDialog | null>> {
  if (HUB_API_MODE === 'mock') {
    return mockResponse(MOCK_DIALOGS.find((d) => d.id === id) ?? null);
  }
  return httpGet<OperatorDialog | null>(`/api/v1/operator/dialogs/${id}`);
}

export async function fetchMessages(dialogId: string): Promise<ApiResponse<OperatorMessage[]>> {
  if (HUB_API_MODE === 'mock') return mockResponse(getMockMessages(dialogId));
  return httpGet<OperatorMessage[]>(`/api/v1/operator/dialogs/${dialogId}/messages`);
}

export async function fetchClientCard(
  dialogId: string
): Promise<ApiResponse<OperatorClientCard | null>> {
  if (HUB_API_MODE === 'mock') return mockResponse(getMockClientCard(dialogId) ?? null);
  return httpGet<OperatorClientCard | null>(`/api/v1/operator/dialogs/${dialogId}/client`);
}

export async function fetchRoutingTimeline(
  dialogId: string
): Promise<ApiResponse<RoutingTimeline>> {
  if (HUB_API_MODE === 'mock') return mockResponse(getMockRoutingTimeline(dialogId));
  return httpGet<RoutingTimeline>(`/api/v1/operator/dialogs/${dialogId}/routing`);
}

export async function fetchChannels(): Promise<ApiResponse<OperatorChannel[]>> {
  if (HUB_API_MODE === 'mock') return mockResponse(MOCK_CHANNELS);
  return httpGet<OperatorChannel[]>('/api/v1/operator/channels');
}

export async function fetchBotScenario(): Promise<ApiResponse<BotScenario>> {
  if (HUB_API_MODE === 'mock') return mockResponse(MOCK_BOT_SCENARIO);
  return httpGet<BotScenario>('/api/v1/operator/bot/scenario');
}

export async function fetchAuditLog(): Promise<ApiResponse<MockAuditEvent[]>> {
  if (HUB_API_MODE === 'mock') return mockResponse([...MOCK_AUDIT_LOG]);
  return httpGet<MockAuditEvent[]>('/api/v1/operator/audit-log');
}

export async function fetchOutbox(): Promise<ApiResponse<MockOutboxMessage[]>> {
  if (HUB_API_MODE === 'mock') return mockResponse([...MOCK_OUTBOX]);
  return httpGet<MockOutboxMessage[]>('/api/v1/operator/outbox');
}

// ─── Public API — actions ─────────────────────────────────────────────────────

/** Assign (or take) a dialog. Returns the updated dialog. */
export async function assignDialog(
  dialogId: string,
  operatorName: string,
  operatorId?: string | null
): Promise<ApiResponse<OperatorDialog>> {
  if (HUB_API_MODE === 'mock') {
    return mockResponse(mockAssign(dialogId, operatorName));
  }
  // Send operatorId alongside operatorName so the backend can derive crmManagerId
  // when it is numeric (e.g. hub-account CRM id = 15744).
  const body: Record<string, string> = { operatorName };
  if (operatorId != null) body.operatorId = operatorId;
  return httpPost<OperatorDialog>(`/api/v1/operator/dialogs/${dialogId}/assign`, body);
}

/** Send an operator message. Returns the new message. */
export async function sendMessage(
  dialogId: string,
  text: string
): Promise<ApiResponse<OperatorMessage>> {
  if (HUB_API_MODE === 'mock') {
    return mockResponse(mockSendMessage(dialogId, text));
  }
  const res = await httpPost<OperatorMessage | { message: OperatorMessage }>(
    `/api/v1/operator/dialogs/${dialogId}/messages`,
    { text }
  );
  if ('message' in res.data) {
    return { data: res.data.message, _source: res._source };
  }
  return res as ApiResponse<OperatorMessage>;
}

/** Transfer dialog to another operator / role / shop. Returns updated dialog. */
export async function transferDialog(
  dialogId: string,
  target: string,
  targetType: 'operator' | 'role' | 'shop' = 'operator',
  reason?: string
): Promise<ApiResponse<OperatorDialog>> {
  if (HUB_API_MODE === 'mock') {
    return mockResponse(mockTransfer(dialogId, target, targetType, reason));
  }
  return httpPost<OperatorDialog>(`/api/v1/operator/dialogs/${dialogId}/transfer`, {
    target,
    targetType,
    reason,
  });
}

/** Escalate dialog by the next routing rule. Returns updated dialog. */
export async function escalateDialog(
  dialogId: string,
  reason?: string
): Promise<ApiResponse<OperatorDialog>> {
  if (HUB_API_MODE === 'mock') {
    return mockResponse(mockEscalate(dialogId, reason));
  }
  return httpPost<OperatorDialog>(`/api/v1/operator/dialogs/${dialogId}/escalate`, { reason });
}

/**
 * Supervisor takes an escalated dialog into work (self-assign).
 *
 * Calls POST /api/v1/supervisor/dialogs/:id/take — the backend is authoritative
 * and uses the JWT token to resolve the supervisor's identity. No body is sent.
 *
 * This is intentionally separate from assignDialog() which POSTs to the operator
 * endpoint and would incorrectly assign the dialog back to the original operator.
 */
export async function takeEscalatedDialog(
  dialogId: string
): Promise<ApiResponse<OperatorDialog>> {
  if (HUB_API_MODE === 'mock') {
    // In mock mode, fall back to mockAssign with a fixed supervisor name
    return mockResponse(mockAssign(dialogId, 'Руководитель'));
  }
  // Body is intentionally empty — backend derives assignee from JWT.
  return httpPost<OperatorDialog>(`/api/v1/supervisor/dialogs/${dialogId}/take`, {});
}

/** Close a dialog. Returns updated dialog. */
export async function closeDialog(
  dialogId: string,
  reason: 'resolved' | 'no_answer' | 'spam' | 'other' = 'resolved'
): Promise<ApiResponse<OperatorDialog>> {
  if (HUB_API_MODE === 'mock') {
    return mockResponse(mockClose(dialogId, reason));
  }
  return httpPost<OperatorDialog>(`/api/v1/operator/dialogs/${dialogId}/close`, { reason });
}

export async function runTelegramDemo(): Promise<ApiResponse<TelegramDemoResult>> {
  if (HUB_API_MODE === 'mock') return mockResponse(runMockTelegramDemo());

  return httpPost<TelegramDemoResult>('/api/v1/webhooks/mock/telegram', {
    update_id: Date.now(),
    message: {
      message_id: 1,
      chat: { id: 'demo-chat' },
      from: { id: 'demo-user', first_name: 'Елена', username: 'elena_demo' },
      contact: { phone_number: '+7 916 234-45-67', first_name: 'Елена' },
      text: 'Магазин. Хочу уточнить стоимость кухни.',
    },
    context: {
      site: 'zov-mebel.ru',
      url: 'https://zov-mebel.ru/catalog/kitchens',
      utm_source: 'cpc',
      selected_channel: 'telegram',
    },
  });
}

// ─── Roles / supervisor / admin (read-only) ──────────────────────────────────

export async function fetchMe(role: RoleCode): Promise<ApiResponse<MeResponse>> {
  if (HUB_API_MODE === 'mock') return mockResponse(getMockMe(role));
  return httpGet<MeResponse>(`/api/v1/roles/me?role=${encodeURIComponent(role)}`);
}

export async function fetchSupervisorDashboard(
  role: RoleCode
): Promise<ApiResponse<SupervisorDashboard>> {
  if (HUB_API_MODE === 'mock') return mockResponse(getMockSupervisorDashboard(role));
  return httpGet<SupervisorDashboard>(
    `/api/v1/supervisor/dashboard?role=${encodeURIComponent(role)}`
  );
}

export async function fetchAdminSettings(): Promise<ApiResponse<AdminSettingsSummary>> {
  if (HUB_API_MODE === 'mock') return mockResponse(getMockAdminSummary());
  return httpGet<AdminSettingsSummary>('/api/v1/admin/settings');
}

/** Full admin dashboard: system/channels/shops/staff/stats/dialogs/integrations. */
export async function fetchAdminDashboard(): Promise<ApiResponse<AdminDashboard>> {
  if (HUB_API_MODE === 'mock') {
    // Build a synthetic dashboard from mock data
    const settings = getMockAdminSummary();
    const mockDashboard: AdminDashboard = {
      system: {
        backend: { status: 'ok', uptime: 0, nodeEnv: 'mock', port: 3001 },
        database: { status: 'warning', note: 'Mock mode — DB not connected' },
        crm: { mode: 'mock', apiUrl: 'https://xzov.ru/api', serviceLogin: 'hub-service', readiness: 'degraded', servicePassword: 'missing' },
        telegram: { mode: 'mock', botTokenSet: false, webhookSecretSet: false, publicUrlSet: false, pollingOrWebhook: 'не проверено', note: 'Токен не задан (mock)' },
        checkedAt: new Date().toISOString(),
      },
      channels: [
        { code: 'telegram', name: 'Telegram', status: 'active', mode: 'mock', comment: 'MVP активен в режиме mock' },
        { code: 'max', name: 'MAX', status: 'next_stage', mode: 'не подключён', comment: 'Следующий этап' },
        { code: 'whatsapp', name: 'WhatsApp', status: 'not_connected', mode: 'не подключён', comment: 'Следующий этап' },
        { code: 'vk', name: 'VK', status: 'not_connected', mode: 'не подключён', comment: 'Следующий этап' },
      ],
      shops: [
        { id: 'shop-ashan-altufyevo', name: 'ТЦ Ашан-Алтуфьево', code: 'АШН', crmId: 57, active: true, operatorsCount: 0 },
        { id: 'shop-filion', name: 'ТЦ Филион', code: 'МФН', crmId: 65, active: true, operatorsCount: 0 },
        { id: 'shop-bibirevo', name: 'м. Бибирево', code: 'МММ', crmId: 54, active: true, operatorsCount: 0 },
        { id: 'shop-polezhaevskaya', name: 'м. Полежаевская', code: 'МПЛ', crmId: 52, active: true, operatorsCount: 0 },
        { id: 'shop-domodedovskaya', name: 'м. Домодедовская', code: 'МОБ', crmId: 53, active: true, operatorsCount: 0 },
        { id: 'shop-skhodnenskaya', name: 'м. Сходненская', code: 'МКП', crmId: 39, active: true, operatorsCount: 0 },
        { id: 'shop-krasnogorsk', name: 'г. Красногорск', code: 'ММТ', crmId: 55, active: true, operatorsCount: 0 },
        { id: 'shop-solntsevo', name: 'м. Солнцево', code: 'МСЛ', crmId: 56, active: true, operatorsCount: 0 },
      ],
      staff: settings.staff,
      stats: { openNow: 0, totalToday: 0, notTaken: 0, inWork: 0, closedToday: 0, escalated: 0, slaOk: 0, slaWarning: 0, slaBreached: 0, calculatedAt: new Date().toISOString() },
      dialogs: [],
      integrations: settings.integrations,
    };
    return mockResponse(mockDashboard);
  }
  return httpGet<AdminDashboard>('/api/v1/admin/dashboard');
}

/**
 * CRM readiness summary. In mock mode the frontend builds a synthetic report
 * locally so the admin view can still render the section without the backend.
 */
export async function fetchReadiness(): Promise<ApiResponse<ReadinessSummary>> {
  if (HUB_API_MODE === 'mock') {
    const checkedAt = new Date().toISOString();
    const summary: ReadinessSummary = {
      mode: 'mock',
      status: 'degraded',
      checkedAt,
      config: {
        mode: 'mock',
        apiUrl: 'https://xzov.ru/api',
        serviceLogin: 'hub-service',
        servicePassword: 'missing',
        requestTimeoutMs: 10000,
      },
      env: [
        { name: 'CRM_USE_MOCKS', required: true, present: true },
        { name: 'CRM_API_URL', required: false, present: true },
        { name: 'CRM_SERVICE_LOGIN', required: false, present: true },
        { name: 'CRM_SERVICE_PASSWORD', required: false, present: false },
      ],
      checks: [
        { id: 'crm.mode', title: 'Режим интеграции с CRM', level: 'ok', note: 'CRM_USE_MOCKS=true — работаем на фикстурах' },
        { id: 'crm.env', title: 'Переменные окружения для боевой CRM', level: 'warning', note: 'Сейчас mock — заполнить до переключения на live: CRM_SERVICE_PASSWORD' },
        { id: 'crm.config', title: 'Конфигурация CRM', level: 'ok', note: 'URL: https://xzov.ru/api, login: hub-service, password: missing' },
      ],
      notes: [
        'День 1 (mock): все probe-запросы используют MockCrmClient.',
        'После доступа от Никиты: CRM_USE_MOCKS=false и POST /api/v1/readiness/crm/probe.',
      ],
    };
    return mockResponse(summary);
  }
  return httpGet<ReadinessSummary>('/api/v1/readiness');
}

// ─── Queue / SLA ─────────────────────────────────────────────────────────────

export async function fetchQueue(role: RoleCode): Promise<ApiResponse<QueueResponse>> {
  if (HUB_API_MODE === 'mock') {
    // Build a queue from the supervisor dashboard fixture so the panel
    // shows realistic SLA badges without backend.
    const sup = getMockSupervisorDashboard(role);
    const target = 5;
    const items = sup.dialogs.map((d) => {
      const minutes = Math.max(0, Math.round((Date.now() - Date.parse(d.updatedAt)) / 60_000));
      const overdue =
        d.status !== 'closed' && d.status !== 'waiting_client' && d.unreadCount > 0 && minutes > target;
      let queueStatus: QueueResponse['items'][number]['queueStatus'] = 'new';
      if (d.status === 'closed') queueStatus = 'closed';
      else if (d.status === 'waiting_client') queueStatus = 'waiting_client';
      else if (d.status === 'assigned') queueStatus = 'in_progress';
      else if (overdue) queueStatus = 'no_answer';
      else if (d.status === 'queued' || d.status === 'escalated') queueStatus = 'open';
      return {
        ...d,
        queueStatus,
        waitingMinutes: minutes,
        slaOverdue: overdue,
        slaTargetMinutes: target,
      };
    });
    const byStatus: QueueResponse['byStatus'] = {
      new: 0, open: 0, in_progress: 0, waiting_client: 0, no_answer: 0, closed: 0,
    };
    for (const i of items) byStatus[i.queueStatus] += 1;
    return mockResponse<QueueResponse>({
      scope: {
        role,
        shopName: sup.scope.shopName,
        visibility: role === 'manager' || role === 'service_manager' ? 'own' : role === 'founder' || role === 'admin' ? 'all' : 'shop',
      },
      total: items.length,
      overdue: items.filter((i) => i.slaOverdue).length,
      byStatus,
      items,
    });
  }
  return httpGet<QueueResponse>(`/api/v1/queue?role=${encodeURIComponent(role)}`);
}

/**
 * Fetch employee stats for the supervisor panel.
 * Uses /api/v1/supervisor/employee-stats which resolves scope from JWT role.
 * Supervisors get shop-scoped stats; admins/founders get global stats.
 * Never falls back to mock operator names (Ирина П. etc.) in http mode.
 */
export async function fetchSupervisorEmployeeStats(
  authRole?: string,
  authName?: string,
  authShopName?: string
): Promise<EmployeeStatsResponse> {
  const isAdmin = authRole === 'admin' || authRole === 'founder' || authRole === 'branch_admin';
  const targetScope: 'shop' | 'all' = isAdmin ? 'all' : 'shop';
  return fetchEmployeeStats(authRole, authName, authShopName, targetScope);
}

// ─── MVP Settings summary ────────────────────────────────────────────────────

export async function fetchSettingsSummary(): Promise<ApiResponse<MvpSettingsSummary>> {
  if (HUB_API_MODE === 'mock') {
    return mockResponse<MvpSettingsSummary>({
      checkedAt: new Date().toISOString(),
      mvp: { ready: false, missing: ['CRM_SERVICE_PASSWORD', 'TELEGRAM_BOT_TOKEN', 'HUB_PUBLIC_URL'] },
      crm: {
        mode: 'mock',
        apiUrl: 'https://xzov.ru/api',
        serviceLogin: 'hub-service',
        servicePassword: 'missing',
        requestTimeoutMs: 10000,
        readiness: 'degraded',
      },
      telegram: {
        mode: 'mock',
        botTokenSet: false,
        webhookSecretSet: false,
        publicUrlSet: false,
        suggestedWebhookUrl: null,
        hint: 'Mock-режим. Заполните TELEGRAM_BOT_TOKEN.',
      },
      sla: { firstResponseTargetMinutes: 5 },
      hub: { nodeEnv: 'development', port: 3001, publicUrl: null },
      requiredEnv: [
        { name: 'CRM_API_URL', set: true },
        { name: 'CRM_SERVICE_LOGIN', set: true },
        { name: 'CRM_SERVICE_PASSWORD', set: false },
        { name: 'CRM_USE_MOCKS', set: true },
        { name: 'TELEGRAM_BOT_TOKEN', set: false },
        { name: 'WEBHOOK_SECRET_TELEGRAM', set: false },
        { name: 'HUB_PUBLIC_URL', set: false },
        { name: 'JWT_SECRET', set: true },
        { name: 'DB_PASS', set: false },
        { name: 'AUTH_TEST_PASSWORD', set: true },
        { name: 'SLA_FIRST_RESPONSE_MINUTES', set: true },
      ],
    });
  }
  return httpGet<MvpSettingsSummary>('/api/v1/settings/summary');
}

// ─── Telegram readiness ──────────────────────────────────────────────────────

export async function fetchTelegramReadiness(): Promise<ApiResponse<TelegramReadiness>> {
  if (HUB_API_MODE === 'mock') {
    return mockResponse<TelegramReadiness>({
      mode: 'mock',
      botTokenSet: false,
      webhookSecretSet: false,
      publicUrlSet: false,
      suggestedWebhookUrl: null,
      hint: 'Mock-режим. Заполните TELEGRAM_BOT_TOKEN и WEBHOOK_SECRET_TELEGRAM, чтобы перейти в live.',
    });
  }
  return httpGet<TelegramReadiness>('/api/v1/telegram/readiness');
}

// ─── Employee daily stats ─────────────────────────────────────────────────────

export interface EmployeeStat {
  operatorName: string;
  dialogsToday: number;
  assigned?: number;
  open?: number;
  closed?: number;
  escalated?: number;
  /** Dialogs currently open (status != closed) */
  openNow: number;
  /** All dialogs touched today */
  totalToday: number;
  /** Display string: "openNow / totalToday" e.g. "2 / 7" */
  displayValue: string;
  /** Shop this employee belongs to */
  shopName?: string | null;
  /** Shift status from availability service */
  shiftStatus?: 'available' | 'break' | 'off';
  /** Human-readable shift label */
  shiftLabel?: string;
}

export interface ShopStatRow {
  shopName: string;
  employees: EmployeeStat[];
  totalOpenNow: number;
  totalToday: number;
  displayValue: string;
}

/** Scoped employee stats response (matches backend envelope). */
export interface EmployeeStatsResponse {
  data: EmployeeStat[];
  /** 'own' | 'shop' | 'all' — visibility scope resolved from JWT role. */
  scope: 'own' | 'shop' | 'all';
  totalDialogsToday: number;
  /** Total open (non-closed) dialogs across all visible items */
  totalOpenNow: number;
  /** Total dialogs today across all visible items */
  totalToday: number;
  /** "totalOpenNow / totalToday" */
  displayValue: string;
  /** Shop-grouped rows for supervisor/admin views */
  rows: ShopStatRow[];
  /** Set for 'own' scope: the authenticated user's name. */
  selfName: string | null;
  generatedAt: string;
  _source: string;
}

/**
 * Fetch scoped employee stats.
 * The backend resolves scope from the JWT Authorization header.
 * In mock mode we synthesise a minimal 'own' response so the UI block renders.
 *
 * @param targetScope - Override the scope: 'own' for operator panel, 'shop'/'all' for supervisor panel.
 *   When omitted, the backend resolves scope from the JWT role.
 */
export async function fetchEmployeeStats(
  authRole?: string,
  authName?: string,
  authShopName?: string,
  targetScope?: 'own' | 'shop' | 'all'
): Promise<EmployeeStatsResponse> {
  if (HUB_API_MODE === 'mock') {
    const isAdmin = authRole === 'admin' || authRole === 'founder' || authRole === 'branch_admin';
    const isSupervisor =
      authRole === 'shop_supervisor' || authRole === 'service_supervisor';

    if (isAdmin) {
      const items = [
        { operatorName: 'Ирина П.', dialogsToday: 5, assigned: 3, open: 1, closed: 1, escalated: 0, openNow: 4, totalToday: 5, displayValue: '4 / 5', shopName: 'м. Солнцево' },
        { operatorName: 'Олег С.', dialogsToday: 3, assigned: 2, open: 0, closed: 1, escalated: 0, openNow: 2, totalToday: 3, displayValue: '2 / 3', shopName: 'Отдел сервиса' },
      ];
      const rows: ShopStatRow[] = [
        { shopName: 'м. Солнцево', employees: [items[0]], totalOpenNow: 4, totalToday: 5, displayValue: '4 / 5' },
        { shopName: 'Отдел сервиса', employees: [items[1]], totalOpenNow: 2, totalToday: 3, displayValue: '2 / 3' },
      ];
      return {
        data: items,
        scope: 'all',
        totalDialogsToday: 8,
        totalOpenNow: 6,
        totalToday: 8,
        displayValue: '6 / 8',
        rows,
        selfName: null,
        generatedAt: new Date().toISOString(),
        _source: 'mock',
      };
    }
    if (isSupervisor) {
      const shopN = authShopName ?? 'Магазин';
      const emp = {
        operatorName: authName ?? 'Оператор',
        dialogsToday: 5,
        assigned: 3,
        open: 1,
        closed: 1,
        escalated: 0,
        openNow: 4,
        totalToday: 5,
        displayValue: '4 / 5',
        shopName: shopN,
      };
      return {
        data: [emp],
        scope: 'shop',
        totalDialogsToday: 5,
        totalOpenNow: 4,
        totalToday: 5,
        displayValue: '4 / 5',
        rows: [{ shopName: shopN, employees: [emp], totalOpenNow: 4, totalToday: 5, displayValue: '4 / 5' }],
        selfName: null,
        generatedAt: new Date().toISOString(),
        _source: 'mock',
      };
    }
    // Operator (manager / service_manager) — own only
    const emp = {
      operatorName: authName ?? 'Я',
      dialogsToday: 5,
      assigned: 3,
      open: 1,
      closed: 1,
      escalated: 0,
      openNow: 4,
      totalToday: 5,
      displayValue: '4 / 5',
      shopName: authShopName ?? null,
    };
    return {
      data: [emp],
      scope: 'own',
      totalDialogsToday: 5,
      totalOpenNow: 4,
      totalToday: 5,
      displayValue: '4 / 5',
      rows: [],
      selfName: authName ?? 'Я',
      generatedAt: new Date().toISOString(),
      _source: 'mock',
    };
  }

  // HTTP mode: backend returns { data, scope, totalDialogsToday, selfName, ... }
  //
  // targetScope determines which endpoint and query we call:
  //   - 'own'       → /api/v1/operator/employee-stats?scope=own  (operator panel)
  //   - 'shop'/'all'→ /api/v1/supervisor/employee-stats          (supervisor panel, role-based)
  //   - undefined   → /api/v1/operator/employee-stats?scope=own  (default: operator panel)
  //
  // For the supervisor panel we call the dedicated /supervisor/employee-stats endpoint
  // which uses the JWT role to determine scope (shop or all) and respects shops[] claim.

  const effectiveScope = targetScope ?? 'own';
  let apiPath: string;
  if (effectiveScope === 'own') {
    apiPath = `/api/v1/operator/employee-stats?scope=own`;
  } else if (effectiveScope === 'shop') {
    apiPath = `/api/v1/supervisor/employee-stats?scope=shop`;
  } else {
    // 'all' scope: supervisor/admin global view
    apiPath = `/api/v1/supervisor/employee-stats?scope=all`;
  }

  const res = await fetch(`${HUB_API_BASE_URL}${apiPath}`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    // In http mode, never synthesise fallback data on error — let the caller handle absence.
    throw new Error(`Hub API error ${res.status}: ${apiPath}`);
  }
  const json = (await res.json()) as EmployeeStatsResponse;

  // For 'own' scope requests: coerce scope label if backend returned something different.
  // This covers hub-account (role=admin) who explicitly requested scope=own in the operator panel.
  if (effectiveScope === 'own' && json.scope !== 'own') {
    json.scope = 'own';
    json.rows = [];
  }

  // Return the backend response as-is. Do NOT filter rows by authName here —
  // the backend is authoritative for which rows belong to the authenticated user.
  return json;
}
